import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { type TodoCatalogFormState, todoCatalogFormSchema } from './template-utils';

interface TodoCatalogTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: TodoCatalogFormState;
  isEditing: boolean;
  isSaving: boolean;
  onSubmit: (form: TodoCatalogFormState) => void;
}

export function TodoCatalogTemplateDialog({
  open,
  onOpenChange,
  form,
  isEditing,
  isSaving,
  onSubmit,
}: TodoCatalogTemplateDialogProps) {
  const formMethods = useForm<TodoCatalogFormState>({
    resolver: zodResolver(todoCatalogFormSchema(isEditing)),
    defaultValues: form,
    mode: 'onChange',
  });
  const { control, formState, handleSubmit, reset, trigger } = formMethods;

  useEffect(() => {
    reset(form);
    void trigger();
  }, [form, reset, trigger]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Breyta verkefnasniði' : 'Nýtt verkefnasnið'}</DialogTitle>
          <DialogDescription>Verkefnasniðmát eru notuð þegar stofnanir virkja verkefnasnið.</DialogDescription>
        </DialogHeader>
        <Form {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={control}
              name="todo_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tegund verkefnis *</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isEditing} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="template_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nafn *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="system_message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kerfisboð *</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[140px]" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="field_specifications"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reitaskilgreiningar (JSON)</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[120px] font-mono text-sm" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={(checked) => field.onChange(checked === true)} />
                  </FormControl>
                  <FormLabel>Virkt</FormLabel>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSaving || !formState.isValid}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Vista
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
